import firebase from "firebase/app";
import "firebase/firestore";

import { estaActivo, estaVencido, vencimientoDe } from "./eventos";

/**
 * Lecturas del portal de clientes.
 *
 * Consultas acotadas a propósito: estas páginas las abre cualquier miembro, no
 * un administrador. Aquí no se usa el módulo Vuex `analytics` (lee colecciones
 * enteras) ni el endpoint `/users/subscriptions` (devuelve el padrón completo
 * con todos los correos).
 */

/** Los tres carriles de contenido que maneja la app. */
export const TIPOS_SESION = [
  { valor: "todas", texto: "Todas" },
  { valor: "conocimiento", texto: "Conocimiento" },
  { valor: "capacitacion", texto: "Capacitación empresarial" },
  { valor: "artistas", texto: "Artistas" },
];

/**
 * Sesiones publicadas. Mismo criterio que la app: sólo `activo === true`, de la
 * más reciente a la más antigua.
 */
export async function cargarSesiones(limite = 60) {
  const snap = await firebase
    .firestore()
    .collection("sesiones")
    .where("activo", "==", true)
    .orderBy("timestamp", "desc")
    .limit(limite)
    .get();

  return snap.docs.map((d) => normalizarSesion(d.id, d.data()));
}

export async function cargarSesion(id) {
  const doc = await firebase.firestore().collection("sesiones").doc(id).get();
  if (!doc.exists) return null;

  const sesion = normalizarSesion(doc.id, doc.data());
  // Una sesión despublicada no debe verse por entrar con la URL directa.
  return sesion.activo ? sesion : null;
}

function normalizarSesion(id, data) {
  return {
    id,
    ...data,
    // La portada llega en campos distintos según cuándo se creó el documento.
    img: data.img || data.imagenUrl || data.imagen || data.thumbnail || data.poster || null,
    tipo: data.type || "conocimiento",
  };
}

/**
 * Directorio de negocios.
 *
 * Ojo con `active`: los negocios dados de alta desde la app no escriben el
 * campo (recnet-24 UserForm/index.js), sólo lo pone el panel. Filtrar por
 * `active === true` escondería a casi todo el directorio, así que la ausencia
 * del campo cuenta como visible.
 */
export async function cargarNegocios() {
  const snap = await firebase.firestore().collection("negocios").get();

  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((n) => n.active !== false)
    .sort((a, b) => String(a.nombre || "").localeCompare(String(b.nombre || "")));
}

/** Eventos vigentes, con la misma regla que el panel y las apps. */
export async function cargarEventos() {
  const snap = await firebase
    .firestore()
    .collection("queplanhoy")
    .orderBy("creadoEn", "desc")
    .get();

  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((e) => estaActivo(e) && !estaVencido(e))
    .map((e) => ({ ...e, vence: vencimientoDe(e) }));
}

/** Vacantes abiertas. Mismo criterio que con los negocios respecto a `active`. */
export async function cargarVacantes() {
  const snap = await firebase
    .firestore()
    .collection("vacantes")
    .orderBy("fechaPublicacion", "desc")
    .get();

  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((v) => v.active !== false);
}

/**
 * Enlace de indicaciones a partir del campo `coordenadas`.
 *
 * El separador es `+`, no una coma: la app guarda `"28.6329+-106.0691"`
 * (`UserForm/index.js` arma `${lat}+-${Math.abs(lng)}`) y al leerlas hace
 * `replace('+', ',')`. Validar sólo el formato con coma hacía que ningún
 * negocio pasara la comprobación y todos cayeran al buscador por dirección,
 * incluso teniendo su ubicación exacta.
 */
export function enlaceMapa(negocio) {
  const destino = coordenadasComoParDecimal(negocio && negocio.coordenadas);
  if (destino) {
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destino)}`;
  }

  const direccion = negocio && negocio.direccion;
  return direccion
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(direccion)}`
    : null;
}

/** Convierte "lat+-lon" (o "lat,lon") en "lat,lon"; null si no es válido. */
export function coordenadasComoParDecimal(valor) {
  const texto = String(valor || "").trim();
  if (!texto) return null;

  // Se parte por la coma o por el `+` que separa ambos números, sin tocar el
  // signo negativo que pueda llevar la longitud.
  const partes = texto.includes(",") ? texto.split(",") : texto.split("+");
  if (partes.length !== 2) return null;

  const lat = Number(partes[0]);
  const lon = Number(partes[1]);
  if (!isFinite(lat) || !isFinite(lon)) return null;
  if (Math.abs(lat) > 90 || Math.abs(lon) > 180) return null;

  return `${lat},${lon}`;
}

/** Enlace de WhatsApp a partir de un teléfono de contacto. */
export function enlaceWhatsapp(telefono, mensaje) {
  const digitos = String(telefono || "").replace(/\D/g, "");
  if (digitos.length < 10) return null;

  const e164 = digitos.length === 10 ? `52${digitos}` : digitos;
  const texto = mensaje ? `?text=${encodeURIComponent(mensaje)}` : "";
  return `https://wa.me/${e164}${texto}`;
}
